import { Node, mergeAttributes } from '@tiptap/core'
import { Suggestion, SuggestionOptions } from './suggestions'

import MentionList from '@/components/common/MentionList.vue'
import { closeAllMentionsTooltips } from '@/components/mention-helper'
import { ChannelState } from '@/composition/channels'
import { GroupState } from '@/composition/groups'
import { GroupMemberStatus } from '@/models/GroupMember'
import { ChannelRoles } from '@/models/Users'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { PluginKey } from '@tiptap/pm/state'
import { VueRenderer } from '@tiptap/vue-3'
import tippy from 'tippy.js'

export type MentionOptions = {
  HTMLAttributes: Record<string, any>
  renderLabel: (props: {
    options: MentionOptions
    node: ProseMirrorNode
  }) => string
  suggestion: Omit<SuggestionOptions, 'editor'>
}

export const MentionPluginKey = new PluginKey('mention')

const broadcastMentions = ['everyone']
export const UserMention = Node.create<MentionOptions>({
  name: 'mention',

  addOptions() {
    return {
      HTMLAttributes: {},
      renderLabel({ node }) {
        if (node.attrs.type === 'broadcast') return `@${node.attrs.label}`
        return `@${node.attrs.label ?? node.attrs.id}`
      },
      suggestion: {
        char: '@',
        allowSpaces: true,
        pluginKey: MentionPluginKey,
        async items({ query }: { query: string; editor: any }) {
          let limit = 10

          const channelVisibility =
            GroupState.state.editorCurrentChannel?.visibility || 'PUBLIC'

          const userChannelRole =
            GroupState.state.editorCurrentChannel?.userRole

          const allowBroadcastMentions =
            channelVisibility === 'PRIVATE'
              ? userChannelRole === ChannelRoles.MANAGER
              : GroupState.state.isGroupAdmin

          if (allowBroadcastMentions) {
            // if the query is empty then set the limit to 4 so that 1st suggestion is of type "everyone"
            if (query.length == 0) {
              limit = 9
            }
          }

          const members =
            channelVisibility === 'PRIVATE'
              ? await ChannelState.fetchChannelMembers(
                  {
                    limit: limit,
                    pageNo: 1,
                  },
                  undefined,
                  query,
                  true,
                  GroupState.state.editorCurrentChannel
                )
              : await GroupState.getGroupMembersDetails(
                  undefined,
                  GroupMemberStatus.ACTIVE,
                  query,
                  {
                    limit: limit,
                    pageNo: 1,
                  },
                  false,
                  false,
                  true
                )

          if (allowBroadcastMentions) {
            if (members.length < 10) {
              // if the members array length is less than 5 then check if the query matches with any of the broadcastMentions
              const filteredBroadcastMentions = broadcastMentions.filter(
                mention => mention.search(query) > -1
              )
              // if there are any matches then push them to members array but the array length should not exceed 5
              if (filteredBroadcastMentions.length > 0) {
                // members array length should not exceed 5
                filteredBroadcastMentions.forEach(mention => {
                  if (members.length < 10) {
                    members.unshift({
                      name: mention,
                      type: 'broadcast',
                    })
                  }
                })
              }
            }
          }
          return members
        },
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter
          const overrideSpace = nodeAfter?.text?.startsWith(' ')

          if (overrideSpace) {
            range.to += 1
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs: props,
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run()

          window.getSelection()?.collapseToEnd()
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from)
          const type = state.schema.nodes[this.name]
          const allow = !!$from.parent.type.contentMatch.matchType(type)
          return allow
        },
        render: () => {
          let component
          let popup
          return {
            onStart: props => {
              component = new VueRenderer(MentionList, {
                props,
                editor: props.editor,
              })

              if (!props.clientRect) {
                return
              }

              popup = tippy('body', {
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'top-start',
                getReferenceClientRect: props.clientRect as any,
              })
            },

            async onUpdate(props) {
              component?.updateProps(props)

              if (!props.clientRect) {
                return
              }

              if (popup.length > 0 && !popup[0].state.isDestroyed) {
                popup[0].setProps({
                  getReferenceClientRect: props.clientRect,
                })
              }
            },

            onKeyDown(props) {
              // Only handle keyboard events if popup exists and is not destroyed
              if (!popup || !popup?.length || popup[0]?.state?.isDestroyed) {
                return false
              }

              if (props.event.key === 'Escape') {
                popup[0].hide()
                return true
              }
              return component?.ref?.onKeyDown(props)
            },

            onExit() {
              popup[0].destroy()
              component.destroy()
            },
          }
        },
      },
    }
  },

  group: 'inline',

  inline: true,

  selectable: false,

  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: attributes => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-id': attributes.id,
          }
        },
      },

      label: {
        default: null,
        parseHTML: element => element.getAttribute('data-label'),
        renderHTML: attributes => {
          if (!attributes.label) {
            return {}
          }

          return {
            'data-label': attributes.label,
          }
        },
      },
      type: {
        default: null,
        parseHTML: element => element.getAttribute('data-mention-type'),
        renderHTML: attributes => {
          if (!attributes.type) {
            return {}
          }

          return {
            'data-mention-type': attributes.type,
          }
        },
      },
      slug: {
        default: null,
        parseHTML: element => element.getAttribute('data-slug'),
        renderHTML: attributes => {
          if (!attributes.slug) {
            return {}
          }

          return {
            'data-slug': attributes.slug,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
        priority: 10001, // higher than link extension's 1000, so on paste it will be treated as mention not link
      },
      {
        tag: `a[data-type="${this.name}"]`,
        priority: 10001, // higher than link extension's 1000, so on paste it will be treated as mention not link
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    // Handle broadcast mentions differently (as spans)
    if (node.attrs.type === 'broadcast') {
      return [
        'span',
        mergeAttributes(
          { 'data-type': this.name },
          this.options.HTMLAttributes,
          HTMLAttributes
        ),
        this.options.renderLabel({
          options: this.options,
          node,
        }),
      ]
    }

    // Regular mentions as anchor tags
    return [
      'a',
      mergeAttributes(
        { 'data-type': this.name },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      this.options.renderLabel({
        options: this.options,
        node,
      }),
    ]
  },

  renderText({ node }) {
    return this.options.renderLabel({
      options: this.options,
      node,
    })
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isMention = false
          const { selection } = state
          const { empty, anchor } = selection

          if (!empty) {
            return false
          }
          closeAllMentionsTooltips()

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isMention = true
              tr.insertText(
                this.options.suggestion.char || '',
                pos,
                pos + node.nodeSize
              )

              return false
            }
          })

          return isMention
        }),
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ]
  },
})
